import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { ToastContainer, Slide } from 'react-toastify';

import config from '../../config';

// Import Style
import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';

// Import Components
import withIntl from '../../components/Intl/WithIntl';
import withRouter from '../../components/Router/WithRouter';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import Menu from './components/Menu/Menu';
import ConversationList from '../Conversation/components/list/ConversationList';
import CmsPanel from '../Cms/components/content/CmsPanel';

import { setSocketUser } from '../../socketListener';

// Import Actions
import { checkVersionRequest, checkServerTimeRequest, initPersistedData, subscribeToChannel, setResubscribeToChannel, initApp, setWindowFocus, isWindowFocus, isAppUpdating, isEmbedded, setRedirect } from './AppActions';
import { getLoggedUser, needAcceptCGU } from '../User/UserActions';
import { loginFromApp } from '../User/AuthActions';
import { initLanguage, getCountryFromLanguage } from '../Intl/IntlActions';
import { displayErrors, removeError } from '../Error/ErrorActions';

import AppCGUAccept from './components/Legal/AppCGUAccept';
import UpdatePage from '../Error/pages/UpdatePage';
import { getConfig } from '../Config/ConfigActions';
import ErrorAlerts from '../Error/components/view/ErrorAlerts';

class App extends PureComponent {
	constructor(props) {
		super(props);

		this.appVersionInterval = null;
		this.appTimeInterval = null;
	}

	componentDidMount() {
		if(!this.props.isMounted) {
			this.props.dispatch(initPersistedData());
			this.initSocket();

			if(this.props.search.sso) {
				this.props.dispatch(loginFromApp(this.props.search.sso));
			} else {
				this.props.dispatch(initApp());
			}

			// Keep user logged in by refreshing token
			if(typeof window !== 'undefined') {
				window.onfocus = event => {
					this.props.dispatch(setWindowFocus(true));
				};
				window.onblur = event => {
					this.props.dispatch(setWindowFocus(false));
				};
			}

			this.appVersionInterval = setInterval(() => {
				this.props.dispatch(checkVersionRequest());
			}, 3 * 60 * 60 * 1000); // 3h

			this.appTimeInterval = setInterval(() => {
				this.props.dispatch(checkServerTimeRequest());
			}, 60 * 60 * 1000); // 1h
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if(this.props.location !== prevProps.location) {
			window.scrollTo(0, 0);
		}

		if(!prevProps.user && this.props.user) {
			this.initSocket();
		}

		// Redirect app if triggered by other component
		if(prevProps.appRedirect !== this.props.appRedirect && this.props.appRedirect) {
			this.props.navigate(this.props.appRedirect);
			this.props.dispatch(setRedirect(''));
		}

		if(!prevProps.needResubscribeToChannels && this.props.needResubscribeToChannels) {
			this.props.channels.forEach(channel => {
				this.props.dispatch(subscribeToChannel(channel, false));
			});
			this.props.dispatch(setResubscribeToChannel(false));
		}
	}

	initSocket = () => {
		this.props.user && setSocketUser(this.props.user);
	};

	componentWillUnmount() {
		console.log('APP::: Unmount');
		// this.props.dispatch(disconnectSocket());
		clearInterval(this.appVersionInterval);
		clearInterval(this.appTimeInterval);
	}

	renderContent = () => {
		const { isMounted, isEmbedded } = this.props;

		return (
			<Fragment>
				{isMounted && !isEmbedded && <Menu />}
				<div id="content-wrapper" className="app-wrapper d-flex flex-column">
					<div id="content">
						<Header />
						{/* Disclaimers authored from the back-office (panel model "disclaimer"), filtered by role & display period */}
						<CmsPanel slug="disclaimers" defaultDisplay={<Fragment />} />

						<Container fluid className="content-wrapper">

							<main id="main-content" className="content my-3">
								{isMounted ? <Outlet /> : <Loader />}
							</main>
						</Container>

						{isMounted && <Footer />}
					</div>

					{isMounted && <ConversationList />}

					<ToastContainer position="top-right" transition={Slide} offset={50} autoClose={10000} limit={5} />
					<ErrorAlerts />
				</div>
			</Fragment>
		);
	};

	renderApp = () => {
		const { needAcceptCGU, isUpdating, isMaintenanceMode } = this.props;
		// console.log('APP', { isUpdating, needAcceptCGU, isMounted: this.props.isMounted, isEmbedded: this.props.isEmbedded });
		if(isUpdating || isMaintenanceMode) {
			return <UpdatePage />;
		}

		if(needAcceptCGU) {
			return (
				<div id="content-wrapper" className="app-wrapper d-flex flex-column">
					<AppCGUAccept />
					<ToastContainer position="top-right" transition={Slide} offset={50} autoClose={10000} limit={5} />
				</div>
			);
		}
		return this.renderContent();
	};

	render() {
		return (
			<div id="wrapper">
				<Helmet
					defaultTitle={`${this.props.intl.formatMessage({ id: 'seoTitle', defaultMessage: 'Online exam test solution' })} - ${config.application.name}`}
					titleTemplate={`%s - ${config.application.name}`}
					meta={[
						{ charset: 'utf-8' },
						{
							'http-equiv': 'X-UA-Compatible',
							content: 'IE=edge',
						},
						{
							name: 'viewport',
							content: 'width=device-width, initial-scale=1, shrink-to-fit=no',
						},
					]}
				/>

				{this.props.isMounted ? this.renderApp() : <Loader />}
			</div>
		);
	}
}

App.defaultProps = {
	user: null,
	needAcceptCGU: false,
	isUpdating: false,
	appRedirect: '',
	channels: [],
	needResubscribeToChannels: false,
	isFocus: false,
	isEmbedded: false,
	isMaintenanceMode: false,
};

App.propTypes = {
	dispatch: PropTypes.func.isRequired,
	location: PropTypes.object.isRequired,
	navigate: PropTypes.func.isRequired,
	search: PropTypes.object.isRequired,
	intl: PropTypes.object.isRequired,
	user: PropTypes.object,
	isMounted: PropTypes.bool.isRequired,
	needAcceptCGU: PropTypes.bool,
	isUpdating: PropTypes.bool,
	appRedirect: PropTypes.string,
	channels: PropTypes.array,
	needResubscribeToChannels: PropTypes.bool,
	isFocus: PropTypes.bool,
	isEmbedded: PropTypes.bool,
	isMaintenanceMode: PropTypes.bool,
};

function mapStateToProps(store, props) {
	return {
		user: getLoggedUser(store),
		isMounted: store.app.isMounted,
		needAcceptCGU: needAcceptCGU(store),
		isUpdating: isAppUpdating(store),
		appRedirect: store.app.redirect,
		channels: store.app.channels,
		needResubscribeToChannels: store.app.needResubscribeToChannels,
		isFocus: isWindowFocus(store),
		isEmbedded: isEmbedded(store),
		isMaintenanceMode: getConfig(store, 'is-maintenance-mode'),
	};
}

App.need = [(params, store, query) => {
	return initLanguage(params._lang || query.lang, null, store);
}];

export default connect(mapStateToProps)(withIntl(withRouter(App)));
